import {
  ECardinality,
  ESdChangeReason,
  EWFAssignmentType,
  IConditionModel,
  ISymbol,
  IWFStep
} from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaRadioGroup from "../../../../ui-kit/kuika-radio-group/kuika-radio-group";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { Comparison } from "../../properties-panel/condition";
import { SymbolPicker } from "../../properties-panel/symbol-picker";
import {
  getBpmnMiState,
  MI_SMI_CANCEL_KEY,
  MI_SMI_CONTINUE_KEY,
  MiInstanceCount,
  readMiCollectionSource,
  readMiCondition,
  readMiExpansionMode,
  readMiInstanceCount,
  readMiOrdering,
  watchBpmnMi,
  writeMiCollectionSource,
  writeMiCondition,
  writeMiExpansionMode,
  writeMiInstanceCount,
  writeMiMode,
  writeMiOrdering
} from "../wf-utils/multi-instance-utils";

type LocalState = {
  inst?: MiInstanceCount;
  collectionSource?: string;
  expansionMode?: string;
  ordering?: string;
};
interface ISmiDecisionProps {
  step: IWFStep | undefined;
}

class SmiDecisionPropertiesBase extends PureSdComponent<
  ISmiDecisionProps & IWithSDProps & IWithContextProp,
  LocalState
> {
  constructor(props: any) {
    super(props);
    this.state = {};
  }

  private disposeWatch?: () => void;

  componentDidMount() {
    const sd: any = (this.props as any).sd;
    const step = this.resolveSelectedStep();
    if (!sd || !step?.id) return;

    // Mirror the diagram marker into mi.mode by its mode name — the one spelling every other writer
    // (settings edits, the AI's MI action) uses. Mirroring isSequential instead stored the same mode
    // as `false` here and `"PMI"` there.
    const mi0 = getBpmnMiState(step.id);
    if (mi0.enabled && mi0.mode) {
      writeMiMode(sd, step, mi0.mode, ESdChangeReason.WFStepMappingChange);
    }

    this.disposeWatch = watchBpmnMi(step.id, async () => {
      const mi = getBpmnMiState(step.id);
      if (!mi.enabled || !mi.mode) return;
      await writeMiMode(sd, step, mi.mode, ESdChangeReason.WFStepMappingChange);
    });
  }

  componentWillUnmount() {
    try {
      this.disposeWatch && this.disposeWatch();
    } catch {}
  }

  private resolveSelectedStep(): IWFStep | undefined {
    const explicit = this.props.step;
    if (explicit?.id) return explicit;
    const sd: any = (this.props as any).sd;
    const viaApi = sd?.getSelectedWfStep?.();
    if (viaApi?.id) return viaApi;
    const selId = sd?.selectedWfNode?.id;
    const nodes = sd?.selectedWf?.nodes;
    if (!selId || !nodes) return undefined;
    const findRec = (n: any, id: string): any | undefined => {
      const steps: any[] = n?.steps ?? [];
      const hit = steps.find((s) => s?.id === id);
      if (hit) return hit;
      for (const s of steps) {
        const inner = s?.subprocess && findRec(s.subprocess, id);
        if (inner) return inner;
      }
      return undefined;
    };
    return findRec(nodes, selId);
  }

  private defaultCondition(): IConditionModel {
    return {
      id: Guid.create().toString(),
      isOrGroup: false,
      conditionSet: { type: ConditionSetType.and, conditions: [] }
    } as IConditionModel;
  }

  private saveContinue = _.debounce(async (c: IConditionModel) => {
    const sd = (this.props as any).sd;
    const step = this.resolveSelectedStep();
    if (!sd || !step) return;
    await writeMiMode(sd, step, "SMI", ESdChangeReason.WFStepMappingChange);
    await writeMiCondition(sd, step, MI_SMI_CONTINUE_KEY, c, ESdChangeReason.WFStepMappingChange);
  }, 150);

  private saveCancel = _.debounce(async (c: IConditionModel) => {
    const sd = (this.props as any).sd;
    const step = this.resolveSelectedStep();
    if (!sd || !step) return;
    await writeMiMode(sd, step, "SMI", ESdChangeReason.WFStepMappingChange);
    await writeMiCondition(sd, step, MI_SMI_CANCEL_KEY, c, ESdChangeReason.WFStepMappingChange);
  }, 150);

  private saveInstanceSymbol = _.debounce(async (symbol: ISymbol) => {
    const sd = (this.props as any).sd;
    const step = this.resolveSelectedStep();
    if (!sd || !step) return;

    const normalized = _.cloneDeep(symbol);
    const raw = (normalized as any).fixedValue;

    if (raw !== undefined && raw !== null) {
      const n = parseInt(raw.toString(), 10);
      if (!Number.isNaN(n) && n < 0) {
        (normalized as any).fixedValue = 0;
      }
    }

    await writeMiMode(sd, step, "SMI", ESdChangeReason.WFStepMappingChange);
    await writeMiInstanceCount(sd, step, { kind: "symbol", value: normalized }, ESdChangeReason.WFStepMappingChange);

    this.setState({ inst: { kind: "symbol", value: normalized } });
  }, 150);

  // The "Based on Step Assignment" row only makes sense once the step has a Role or Dynamic
  // assignment; until then it stays visible but disabled, and says why.
  private instanceSourceOptions(canUseStepAssignment: boolean): KuikaSelectboxOption[] {
    return [
      { value: "Manual", label: "Manual (Number / Symbol)" },
      {
        value: "Assignment",
        disabled: !canUseStepAssignment,
        label: `Based on Step Assignment${canUseStepAssignment ? "" : " (Requires Role or Dynamic)"}`
      }
    ];
  }

  private renderInstanceCount(step: IWFStep) {
    const saved = this.state.inst ?? (readMiInstanceCount(step) as MiInstanceCount | undefined);
    const initialSymbol = saved?.kind === "symbol" ? (saved.value as ISymbol) : undefined;

    const source = this.state.collectionSource ?? readMiCollectionSource(step);
    const expansion = this.state.expansionMode ?? readMiExpansionMode(step);
    const ordering = this.state.ordering ?? readMiOrdering(step);

    const canUseStepAssignment =
      step.assignment?.assignmentType === EWFAssignmentType.dynamic ||
      step.assignment?.assignmentType === EWFAssignmentType.role;

    return (
      <section className="kuika-wf-step__section" data-testid="smi-instance-count">
        <div className="kuika-wf-step__section-head">
          <KuikaSvg
            name="repeat"
            width={14}
            height={14}
            color="var(--dark-9)"
            className="kuika-wf-step__section-icon"
          />
          <span className="kuika-wf-step__section-title">Instance Generation</span>
        </div>
        <p className="kuika-wf-step__hint">Sequential Instance Count Strategy</p>

        <KuikaSelectbox
          className="kuika-wf-step__select"
          size="sm"
          value={source}
          onChange={async (val: string) => {
            const next: "Manual" | "Assignment" = val === "Assignment" ? "Assignment" : "Manual";
            this.setState({ collectionSource: next });
            const sd = (this.props as any).sd;
            await writeMiCollectionSource(sd, step, next, ESdChangeReason.WFStepMappingChange);
            await writeMiMode(sd, step, "SMI", ESdChangeReason.WFStepMappingChange);
          }}
          options={this.instanceSourceOptions(canUseStepAssignment)}
        />

        {source === "Manual" && (
          <div>
            <span className="kuika-wf-step__form-label">Instance Count (Fixed/Variable)</span>
            <SymbolPicker
              changeType="onBlur"
              symbol={initialSymbol}
              valueType={"Int32"}
              targetCardinality={ECardinality.One}
              hideWFParams={false}
              hideMiInstanceCountParam={true}
              minValue={0}
              onSelectedSymbol={(symbol: ISymbol) => this.saveInstanceSymbol(symbol)}
            />
          </div>
        )}

        {source === "Assignment" && (
          <div className="kuika-wf-step__card">
            <span className="kuika-wf-step__form-label">Assignment Expansion</span>
            <KuikaRadioGroup
              options={[
                { label: "By Role (One instance per Role)", value: "ByRole" },
                { label: "By User (One instance per User in Roles)", value: "ByUser" }
              ]}
              value={expansion}
              onChange={async (val: string | number) => {
                const mode: "ByRole" | "ByUser" = val === "ByUser" ? "ByUser" : "ByRole";
                this.setState({ expansionMode: mode });
                const sd = (this.props as any).sd;
                await writeMiExpansionMode(sd, step, mode, ESdChangeReason.WFStepMappingChange);
              }}
            />
            <div className="kuika-wf-step__card-split">
              <span className="kuika-wf-step__form-label">Execution Order</span>
              <KuikaSelectbox
                className="kuika-wf-step__select"
                size="sm"
                value={ordering}
                onChange={async (val: string) => {
                  const next: "Ascending" | "Descending" = val === "Descending" ? "Descending" : "Ascending";
                  this.setState({ ordering: next });
                  const sd = (this.props as any).sd;
                  await writeMiOrdering(sd, step, next, ESdChangeReason.WFStepMappingChange);
                }}
                options={[
                  { value: "Ascending", label: "Ascending (First to Last)" },
                  { value: "Descending", label: "Descending (Last to First)" }
                ]}
              />
            </div>
          </div>
        )}
      </section>
    );
  }

  private renderCancel(step: IWFStep) {
    let cond = readMiCondition(step, MI_SMI_CANCEL_KEY) as IConditionModel | undefined;
    if (!cond) cond = this.defaultCondition();
    return (
      <section className="kuika-wf-step__section" data-testid="smi-cancel">
        <div className="kuika-wf-step__section-head">
          <KuikaSvg name="x" width={14} height={14} color="var(--dark-9)" className="kuika-wf-step__section-icon" />
          <span className="kuika-wf-step__section-title">Cancel Condition</span>
        </div>
        <p className="kuika-wf-step__hint">
          If the condition is met, the remaining instances in the SMI are terminated without being run.
        </p>

        <Comparison
          condition={cond}
          onChange={(c) => this.saveCancel(c)}
          conditionType="workflow"
          executePointer={step.name}
          showHeader={false}
        />
      </section>
    );
  }

  render() {
    const step = this.resolveSelectedStep();
    if (!step?.id) return null;
    const mi = getBpmnMiState(step.id);
    if (!(mi.enabled && mi.mode === "SMI")) return null;

    return (
      <>
        {this.renderInstanceCount(step)}
        {this.renderCancel(step)}
      </>
    );
  }

  checkNeedRender = (): boolean => true;
}

const SmiDecisionProperties = withContext<ISmiDecisionProps>(withSD<ISmiDecisionProps>(SmiDecisionPropertiesBase));
export { SmiDecisionProperties };
